/**
 * CameraOverlayRenderer Component
 *
 * Renders camera movement overlays on top of storyboard images.
 * Overlays use normalized 0-1 coordinates that are converted to absolute
 * pixel positions based on the container size.
 *
 * Features:
 * - Converts normalized coordinates (0-1) to absolute pixels
 * - Supports both SVG symbols and image-based overlays
 * - Handles rotation and scaling transformations
 * - Supports preset positions with automatic positioning
 * - Centers overlays on their position point
 * - Non-interactive (pointerEvents: none) so image interactions work
 */

import React, { CSSProperties } from 'react';
import type { CameraOverlay } from '@/types';
import { overlaySymbols } from './overlayLibrary';
import { presetPositionToCoordinates } from '@/utils/overlayPositions';

export interface CameraOverlayRendererProps {
  /** Array of overlays to render */
  overlays: CameraOverlay[];
  /** Container width in pixels for coordinate conversion */
  containerWidth: number;
  /** Container height in pixels for coordinate conversion */
  containerHeight: number;
}

/**
 * CameraOverlayRenderer Component
 *
 * Renders SVG overlays absolutely positioned on the image container
 */
export function CameraOverlayRenderer({
  overlays,
  containerWidth,
  containerHeight,
}: CameraOverlayRendererProps) {
  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const containerStyles: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none', // Don't block image interactions
    zIndex: 10, // Above image, below UI overlays
  };

  // ---------------------------------------------------------------------------
  // RENDER OVERLAYS
  // ---------------------------------------------------------------------------

  return (
    <div style={containerStyles}>
      {overlays.map((overlay) => {
        // Get overlay properties with defaults
        const rotation = overlay.rotation || 0;
        const userScale = overlay.scale || 1;
        const opacity = overlay.opacity !== undefined ? overlay.opacity : 0.8;
        const color = overlay.color;

        // Calculate base size proportionally to container width
        // Base size is 80px for ~800px container width, scales proportionally
        const baseSize = 80;
        const containerScaleFactor = containerWidth / 800;
        const scaledSize = baseSize * containerScaleFactor * userScale;

        // For zoom-in and zoom-out, render frame with arrows
        if (overlay.type === 'zoom-in' || overlay.type === 'zoom-out') {
          const framePos = overlay.framePosition || { x: 0.5, y: 0.5 };
          const frameSize = overlay.frameSize || 0.5;

          // Calculate frame dimensions
          const frameWidth = containerWidth * frameSize;
          const frameHeight = containerHeight * frameSize;
          const frameCenterX = framePos.x * containerWidth;
          const frameCenterY = framePos.y * containerHeight;
          const frameLeft = frameCenterX - frameWidth / 2;
          const frameTop = frameCenterY - frameHeight / 2;

          // Frame corners (absolute positions)
          const frameCorners = {
            topLeft: { x: frameLeft, y: frameTop },
            topRight: { x: frameLeft + frameWidth, y: frameTop },
            bottomRight: { x: frameLeft + frameWidth, y: frameTop + frameHeight },
            bottomLeft: { x: frameLeft, y: frameTop + frameHeight },
          };

          // Container corners (absolute positions)
          const containerCorners = {
            topLeft: { x: 0, y: 0 },
            topRight: { x: containerWidth, y: 0 },
            bottomRight: { x: containerWidth, y: containerHeight },
            bottomLeft: { x: 0, y: containerHeight },
          };

          const strokeColor = overlay.color || '#FFFFFF';
          const strokeWidth = 2 * containerScaleFactor;

          const arrowSize = 20 * containerScaleFactor; // Doubled from 10 to 20
          const isZoomIn = overlay.type === 'zoom-in';

          // Helper function to create arrow marker
          const createArrow = (fromX: number, fromY: number, toX: number, toY: number, id: string) => {
            // Calculate angle
            const angle = Math.atan2(toY - fromY, toX - fromX);
            const arrowX = toX - Math.cos(angle) * arrowSize;
            const arrowY = toY - Math.sin(angle) * arrowSize;

            return (
              <g key={id}>
                <line
                  x1={fromX}
                  y1={fromY}
                  x2={toX}
                  y2={toY}
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  strokeDasharray="5,5"
                />
                {/* Arrow head */}
                <polygon
                  points={`
                    ${toX},${toY}
                    ${arrowX - Math.sin(angle) * arrowSize / 2},${arrowY + Math.cos(angle) * arrowSize / 2}
                    ${arrowX + Math.sin(angle) * arrowSize / 2},${arrowY - Math.cos(angle) * arrowSize / 2}
                  `}
                  fill={strokeColor}
                />
              </g>
            );
          };

          return (
            <React.Fragment key={overlay.id}>
              <svg
                width={containerWidth}
                height={containerHeight}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  pointerEvents: 'none',
                  opacity,
                }}
              >
                {/* Draw arrows - direction depends on zoom type */}
                {isZoomIn ? (
                  <>
                    {/* Zoom In: arrows point FROM corners TO frame */}
                    {createArrow(containerCorners.topLeft.x, containerCorners.topLeft.y, frameCorners.topLeft.x, frameCorners.topLeft.y, 'tl')}
                    {createArrow(containerCorners.topRight.x, containerCorners.topRight.y, frameCorners.topRight.x, frameCorners.topRight.y, 'tr')}
                    {createArrow(containerCorners.bottomRight.x, containerCorners.bottomRight.y, frameCorners.bottomRight.x, frameCorners.bottomRight.y, 'br')}
                    {createArrow(containerCorners.bottomLeft.x, containerCorners.bottomLeft.y, frameCorners.bottomLeft.x, frameCorners.bottomLeft.y, 'bl')}
                  </>
                ) : (
                  <>
                    {/* Zoom Out: arrows point FROM frame TO corners */}
                    {createArrow(frameCorners.topLeft.x, frameCorners.topLeft.y, containerCorners.topLeft.x, containerCorners.topLeft.y, 'tl')}
                    {createArrow(frameCorners.topRight.x, frameCorners.topRight.y, containerCorners.topRight.x, containerCorners.topRight.y, 'tr')}
                    {createArrow(frameCorners.bottomRight.x, frameCorners.bottomRight.y, containerCorners.bottomRight.x, containerCorners.bottomRight.y, 'br')}
                    {createArrow(frameCorners.bottomLeft.x, frameCorners.bottomLeft.y, containerCorners.bottomLeft.x, containerCorners.bottomLeft.y, 'bl')}
                  </>
                )}

                {/* Draw the frame rectangle */}
                <rect
                  x={frameLeft}
                  y={frameTop}
                  width={frameWidth}
                  height={frameHeight}
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth={strokeWidth * 1.5}
                />
              </svg>
            </React.Fragment>
          );
        }

        // For frame overlay, render just the rectangle (no arrows)
        if (overlay.type === 'frame') {
          const framePos = overlay.framePosition || { x: 0.5, y: 0.5 };
          const frameSize = overlay.frameSize || 0.5;

          // Calculate frame dimensions
          const frameWidth = containerWidth * frameSize;
          const frameHeight = containerHeight * frameSize;
          const frameCenterX = framePos.x * containerWidth;
          const frameCenterY = framePos.y * containerHeight;
          const frameLeft = frameCenterX - frameWidth / 2;
          const frameTop = frameCenterY - frameHeight / 2;

          const strokeColor = overlay.color || '#FFFFFF';
          const strokeWidth = 2 * containerScaleFactor;

          return (
            <React.Fragment key={overlay.id}>
              <svg
                width={containerWidth}
                height={containerHeight}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  pointerEvents: 'none',
                  opacity,
                }}
              >
                {/* Draw the frame rectangle */}
                <rect
                  x={frameLeft}
                  y={frameTop}
                  width={frameWidth}
                  height={frameHeight}
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth={strokeWidth * 1.5}
                />
              </svg>
            </React.Fragment>
          );
        }

        // For push-in and pull-out, render 4 corner arrows
        if (overlay.type === 'push-in' || overlay.type === 'pull-out') {
          if (!overlay.imagePath) return null;

          const imageSrc = `/overlay-assets/${overlay.imagePath}`;
          const cornerPositions: Array<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'> = [
            'top-left',
            'top-right',
            'bottom-right',
            'bottom-left',
          ];

          // Mirror transforms for each corner (flip horizontally/vertically)
          // Assumes base arrow points from top-left toward bottom-right
          const cornerTransforms: Record<string, string> = {
            'top-left': 'translate(-50%, -50%)',                    // No flip
            'top-right': 'translate(-50%, -50%) scaleX(-1)',        // Flip horizontal
            'bottom-right': 'translate(-50%, -50%) scale(-1, -1)',  // Flip both
            'bottom-left': 'translate(-50%, -50%) scaleY(-1)',      // Flip vertical
          };

          return (
            <React.Fragment key={overlay.id}>
              {cornerPositions.map((corner) => {
                const coords = presetPositionToCoordinates(corner);
                const absoluteX = coords.x * containerWidth;
                const absoluteY = coords.y * containerHeight;
                const arrowTransform = cornerTransforms[corner];

                const overlayContainerStyles: CSSProperties = {
                  position: 'absolute',
                  left: absoluteX,
                  top: absoluteY,
                  width: scaledSize,
                  height: scaledSize,
                  transform: arrowTransform,
                  transformOrigin: 'center',
                  transition: 'none',
                };

                return (
                  <div
                    key={`${overlay.id}-${corner}`}
                    style={overlayContainerStyles}
                    title={`Camera overlay: ${overlay.type}`}
                  >
                    <img
                      src={imageSrc}
                      alt={overlay.type}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        opacity,
                      }}
                    />
                  </div>
                );
              })}
            </React.Fragment>
          );
        }

        // For truck-left and truck-right, render arrow stretched across full width at bottom
        if (overlay.type === 'truck-left' || overlay.type === 'truck-right') {
          if (!overlay.imagePath) return null;

          const imageSrc = `/overlay-assets/${overlay.imagePath}`;

          // Scale both width and height based on user's scale setting
          // Base dimensions: full container width, with proportional height
          const baseHeight = containerWidth * 0.08;
          const scaledWidth = containerWidth * userScale;
          const scaledHeight = baseHeight * userScale;

          // Position at bottom center
          const bottomY = containerHeight - scaledHeight / 2;

          // Mirror for truck-right
          const mirrorTransform = overlay.type === 'truck-right' ? 'scaleX(-1)' : '';
          const combinedTransform = `translate(-50%, -50%) ${mirrorTransform}`;

          const overlayContainerStyles: CSSProperties = {
            position: 'absolute',
            left: containerWidth / 2,
            top: bottomY,
            width: scaledWidth, // Scale width with user scale
            height: scaledHeight,
            transform: combinedTransform,
            transformOrigin: 'center',
            transition: 'none',
          };

          return (
            <div
              key={overlay.id}
              style={overlayContainerStyles}
              title={`Camera overlay: ${overlay.type}`}
            >
              <img
                src={imageSrc}
                alt={overlay.type}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill', // Stretch to fill container
                  opacity,
                }}
              />
            </div>
          );
        }

        // For pan-left and pan-right, render curved arrow stretched across full width at bottom
        if (overlay.type === 'pan-left' || overlay.type === 'pan-right') {
          if (!overlay.imagePath) return null;

          const imageSrc = `/overlay-assets/${overlay.imagePath}`;

          // Scale both width and height based on user's scale setting
          // Base dimensions: full container width, with proportional height
          const baseHeight = containerWidth * 0.08;
          const scaledWidth = containerWidth * userScale;
          const scaledHeight = baseHeight * userScale;

          // Position at bottom center
          const bottomY = containerHeight - scaledHeight / 2;

          // Mirror for pan-right
          const mirrorTransform = overlay.type === 'pan-right' ? 'scaleX(-1)' : '';
          const combinedTransform = `translate(-50%, -50%) ${mirrorTransform}`;

          const overlayContainerStyles: CSSProperties = {
            position: 'absolute',
            left: containerWidth / 2,
            top: bottomY,
            width: scaledWidth, // Scale width with user scale
            height: scaledHeight,
            transform: combinedTransform,
            transformOrigin: 'center',
            transition: 'none',
          };

          return (
            <div
              key={overlay.id}
              style={overlayContainerStyles}
              title={`Camera overlay: ${overlay.type}`}
            >
              <img
                src={imageSrc}
                alt={overlay.type}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill', // Stretch to fill container
                  opacity,
                }}
              />
            </div>
          );
        }

        // For tilt-up and tilt-down, render curved arrow stretched vertically on the right side
        if (overlay.type === 'tilt-up' || overlay.type === 'tilt-down') {
          if (!overlay.imagePath) return null;

          const imageSrc = `/overlay-assets/${overlay.imagePath}`;

          // Scale both width and height based on user's scale setting
          // Arrow is 200x1080px (aspect ratio: 200/1080 = 0.185)
          // Height stretches to full container height, width maintains aspect ratio
          const imageAspectRatio = 200 / 1080;
          const baseWidth = containerHeight * imageAspectRatio;
          const scaledWidth = baseWidth * userScale;
          const scaledHeight = containerHeight * userScale;

          // Position on right side, vertically centered
          const rightX = containerWidth - scaledWidth / 2;

          // Mirror vertically for tilt-down
          const mirrorTransform = overlay.type === 'tilt-down' ? 'scaleY(-1)' : '';
          const combinedTransform = `translate(-50%, -50%) ${mirrorTransform}`;

          const overlayContainerStyles: CSSProperties = {
            position: 'absolute',
            left: rightX,
            top: containerHeight / 2,
            width: scaledWidth,
            height: scaledHeight, // Scale height with user scale
            transform: combinedTransform,
            transformOrigin: 'center',
            transition: 'none',
          };

          return (
            <div
              key={overlay.id}
              style={overlayContainerStyles}
              title={`Camera overlay: ${overlay.type}`}
            >
              <img
                src={imageSrc}
                alt={overlay.type}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill', // Stretch to fill container
                  opacity,
                }}
              />
            </div>
          );
        }

        // For boom-up and boom-down, render straight arrow stretched vertically on the right side
        if (overlay.type === 'boom-up' || overlay.type === 'boom-down') {
          if (!overlay.imagePath) return null;

          const imageSrc = `/overlay-assets/${overlay.imagePath}`;

          // Scale both width and height based on user's scale setting
          // Arrow is straight vertical (assume similar aspect ratio to arrow_up.png)
          // Height stretches to full container height, width maintains aspect ratio
          const imageAspectRatio = 200 / 1080; // Same as curved arrow
          const baseWidth = containerHeight * imageAspectRatio;
          const scaledWidth = baseWidth * userScale;
          const scaledHeight = containerHeight * userScale;

          // Position on right side, vertically centered
          const rightX = containerWidth - scaledWidth / 2;

          // Mirror vertically for boom-down
          const mirrorTransform = overlay.type === 'boom-down' ? 'scaleY(-1)' : '';
          const combinedTransform = `translate(-50%, -50%) ${mirrorTransform}`;

          const overlayContainerStyles: CSSProperties = {
            position: 'absolute',
            left: rightX,
            top: containerHeight / 2,
            width: scaledWidth,
            height: scaledHeight,
            transform: combinedTransform,
            transformOrigin: 'center',
            transition: 'none',
          };

          return (
            <div
              key={overlay.id}
              style={overlayContainerStyles}
              title={`Camera overlay: ${overlay.type}`}
            >
              <img
                src={imageSrc}
                alt={overlay.type}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill', // Stretch to fill container
                  opacity,
                }}
              />
            </div>
          );
        }

        // For single-position overlays
        // Determine position (preset overrides manual x/y)
        let posX = overlay.position.x;
        let posY = overlay.position.y;

        if (overlay.presetPosition) {
          const presetCoords = presetPositionToCoordinates(overlay.presetPosition);
          posX = presetCoords.x;
          posY = presetCoords.y;
        }

        // Convert normalized 0-1 coordinates to absolute pixels
        const absoluteX = posX * containerWidth;
        const absoluteY = posY * containerHeight;

        // Individual overlay container styles
        const overlayContainerStyles: CSSProperties = {
          position: 'absolute',
          left: absoluteX,
          top: absoluteY,
          width: scaledSize,
          height: scaledSize,
          // Transform: translate to center on position, then rotate
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
          transformOrigin: 'center',
          transition: 'none', // No animations for performance
        };

        // Render image-based overlay if imagePath is provided
        if (overlay.imagePath) {
          const imageSrc = `/overlay-assets/${overlay.imagePath}`;

          return (
            <div
              key={overlay.id}
              style={overlayContainerStyles}
              title={`Camera overlay: ${overlay.type}`}
            >
              <img
                src={imageSrc}
                alt={overlay.type}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  opacity,
                }}
              />
            </div>
          );
        }

        // Otherwise, render SVG symbol overlay
        const OverlaySymbol = overlaySymbols[overlay.type];

        if (!OverlaySymbol) {
          console.warn(`Unknown overlay type: ${overlay.type}`);
          return null;
        }

        return (
          <div
            key={overlay.id}
            style={overlayContainerStyles}
            title={`Camera overlay: ${overlay.type}`}
          >
            <OverlaySymbol
              color={color}
              opacity={opacity}
              size={scaledSize}
            />
          </div>
        );
      })}
    </div>
  );
}

export default CameraOverlayRenderer;
